'use client';

import React, { ButtonHTMLAttributes } from 'react';

import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import CheckIcon from '@flipster/react/icon/generated/CheckIcon';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  tooltip?: string;
  withChevron?: boolean;
  isOpen?: boolean;
  css?: Styles;
}

export const Button = ({ active, tooltip, withChevron, isOpen, children, css: cssProps, ...props }: ButtonProps) => {
  const button = (
    <button
      type='button'
      {...props}
      className={css(
        center.raw({ h: '100%', px: 6, color: 'content.default.level-2', gap: 4 }),
        active && { color: 'content.accent.info' },
        isOpen && { color: 'content.default.level-5' },
        withChevron && isOpen && { '& > svg': { transform: 'rotate(-180deg)' } },
        cssProps,
      )}
    >
      {children}
      {withChevron && <ChevronDownIcon size={12} />}
    </button>
  );

  if (tooltip)
    return (
      <Tooltip
        placement='bottom'
        shape='white'
        mobileAlert={() => {
          /* 모바일에서는 툴팁 감추기 용으로 사용 mobileAlert 이 있을땐 touch 시 툴팁이 뜨지않고 함수가 호출됨 */
        }}
      >
        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
        <Tooltip.Content>{tooltip}</Tooltip.Content>
      </Tooltip>
    );

  return button;
};

interface CheckItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

export const CheckItem = ({ checked, onChange, onClick, children, ...buttonProps }: CheckItemProps) => {
  return (
    <button
      type='button'
      role='checkbox'
      aria-checked={checked}
      data-checked={checked}
      {...buttonProps}
      onClick={(e) => {
        const currentlyChecked = e.currentTarget.dataset.checked === 'true';
        onChange?.(!currentlyChecked);
        onClick?.(e);
      }}
      className={css(
        hstack.raw({
          pos: 'relative',
          textStyle: 'Text/M/Regular',
          color: 'content.default.level-5',
          textAlign: 'left',
          gap: 6,
          w: '100%',
          py: 2,
          pl: 2,
          pr: 34,
          rounded: 4,
          _hover: {
            bgColor: 'background.interaction.hover',
          },
        }),
        checked && { color: 'content.accent.info' },
      )}
    >
      {children}
      {checked && (
        <CheckIcon
          className={css({ pos: 'absolute', top: '50%', right: 4, h: 18, w: 18, transform: 'translateY(-50%)' })}
        />
      )}
    </button>
  );
};
